import React from 'react';
import { useState } from 'react';
import { StoneAgeUsersTable } from './StoneAgeUsersTable';
import { User } from '../../models/user';
import { updateUser } from '../../utils/api/users';
import { EditUserModal } from './EditUserModal';
import { useIntl } from 'react-intl';
import { ProfileMessages } from '../../intl/ProfileMessages';
import { useListOrganizationsV2Query } from '../../redux/api/organizations';
import { Roles } from '@dataquiver/stoneage-roles';
import { getOrgName } from '../../utils/helpers/referenceHelpers';
import { SearchUsersRequest, useSearchUsersQuery } from '../../redux/api/users';
import { createNextState } from '@reduxjs/toolkit';

interface StoneAgeUsersTableCardProps {
  setToast: (success: boolean, message: string) => void;
}

export const StoneAgeUsersTableCard: React.FC<StoneAgeUsersTableCardProps> = ({ setToast }) => {
  const { formatMessage } = useIntl();

  const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
  const [userRequest, setUserRequest] = useState<SearchUsersRequest>({
    pageNumber: 0,
    pageSize: 10,
    roles: [40, 50],
    statuses: ['active'],
    sort: { order: 'desc', property: 'family_name' },
  });
  const { data: organizations, isLoading: orgsLoading } = useListOrganizationsV2Query({
    pageNumber: 0,
    pageSize: 1000,
    statuses: ['active'],
  });

  // Fetch StoneAge Admin and StoneAge Dev users.
  const { data: userResults, isLoading: usersLoading } = useSearchUsersQuery(userRequest);

  const handleEdit = (user: User): void => {
    if (!user) {
      return;
    }

    setEditingUser(user);
  };

  const handleSave = async (role?: number) => {
    if (!editingUser) {
      return;
    }
    if (!role) {
      setEditingUser(undefined);
      return;
    }

    try {
      await updateUser(editingUser._id, {
        role,
      });

      setToast(
        true,
        formatMessage(ProfileMessages.userRoleSetSuccessfully, {
          name: `${editingUser.given_name} ${editingUser.family_name}`,
          role: Roles.getPrettyPrint(role),
        }),
      );
    } catch (err) {
      setToast(
        false,
        formatMessage(ProfileMessages.userRoleSetUnsuccessful, {
          name: `${editingUser.given_name} ${editingUser.family_name}`,
          role: Roles.getPrettyPrint(role),
        }),
      );
    }

    setEditingUser(undefined);
  };

  const handleQueryChange = (requestUpdates: Partial<SearchUsersRequest>) => {
    setUserRequest(
      createNextState(draft => {
        Object.assign(draft, requestUpdates);
      }),
    );
  };

  return (
    <>
      <StoneAgeUsersTable
        loading={usersLoading || orgsLoading}
        onSelection={handleEdit}
        organizations={organizations ?? []}
        data={userResults}
        request={userRequest}
        onTableChange={handleQueryChange}
      />
      <EditUserModal
        isOpen={!!editingUser}
        onSave={handleSave}
        user={editingUser}
        userOrganizationName={getOrgName(editingUser?.org, organizations ?? [])}
      />
    </>
  );
};
